import { Box } from '@primer/react';
import { E2eTestElementId } from '@upright/test-ids/dom-elements';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useIsTeam } from '@upright/ui/common/generic/hooks';
import { sortedUniq } from 'lodash';
import styled, { css } from 'styled-components';
import { TableV2PaginationEventHandlers } from '../../types';

export interface PaginatorProps {
  enablePagination: boolean;
  gotoPage: (updater: number | ((pageIndex: number) => number)) => void;
  nextPage: () => void;
  previousPage: () => void;
  canNextPage: boolean;
  canPreviousPage: boolean;
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  setPageSize: (updater: number | ((pageSize: number) => number)) => void;
  eventHandlers?: TableV2PaginationEventHandlers;
}

// TODO: Figure out how/where to type isFetchingPageData
export function Paginator(
  props: PaginatorProps & {
    isFetchingPageData: boolean;
    showPageSizeSelector: 'always' | 'never' | 'teamOnly';
    pageSizeOptions?: number[];
  },
) {
  const { eventHandlers } = props;

  const showPageSizeSelector = useShowPageSizeSelector(props.showPageSizeSelector);

  // We substitute 0 with 1 to avoid showing "Page 1 of 0" when there's no data.
  const pageCountToShow = props.pageCount || 1;
  return (
    <Root>
      {showPageSizeSelector && (
        <>
          <PageControlWrap>
            <PageSizeSelector value={props.pageSize} setValue={props.setPageSize} options={props.pageSizeOptions} />
          </PageControlWrap>
          <Separator />
        </>
      )}
      <PageControlWrap data-testid={E2eTestElementId.PAGINATION_CURRENT_PAGE}>
        Page {props.pageIndex + 1} of {pageCountToShow}
      </PageControlWrap>
      <JumpToEdgeButton
        onClick={() => {
          if (props.canPreviousPage) {
            if (eventHandlers?.onGotoFirstPage) eventHandlers.onGotoFirstPage();
            props.gotoPage(0);
          }
        }}
        enabled={props.canPreviousPage}
        disabled={!props.canPreviousPage}
      >
        <ChevronIconLeft />
      </JumpToEdgeButton>
      <PrevButton
        onClick={() => {
          if (props.canPreviousPage) {
            if (eventHandlers?.onGotoPreviousPage) eventHandlers.onGotoPreviousPage(props.pageIndex - 1);
            props.previousPage();
          }
        }}
        enabled={props.canPreviousPage && !props.isFetchingPageData}
        disabled={!(props.canPreviousPage && !props.isFetchingPageData)}
      >
        Prev
      </PrevButton>
      <NextButton
        onClick={() => {
          if (props.canNextPage) {
            if (eventHandlers?.onGotoNextPage) eventHandlers.onGotoNextPage(props.pageIndex + 1);
            props.nextPage();
          }
        }}
        enabled={props.canNextPage && !props.isFetchingPageData}
        disabled={!(props.canNextPage && !props.isFetchingPageData)}
      >
        Next
      </NextButton>
      <JumpToEdgeButton
        onClick={() => {
          if (props.canNextPage) {
            if (eventHandlers?.onGotoLastPage) eventHandlers.onGotoLastPage(props.pageCount - 1);
            props.gotoPage(props.pageCount - 1);
          }
        }}
        enabled={props.canNextPage}
        disabled={!props.canNextPage}
      >
        <ChevronIconRight />
      </JumpToEdgeButton>
    </Root>
  );
}

function useShowPageSizeSelector(config: 'always' | 'never' | 'teamOnly') {
  const isTeam = useIsTeam();

  switch (config) {
    case 'always':
      return true;
    case 'never':
      return false;
    case 'teamOnly':
      return isTeam;
  }
}

function Separator() {
  return <Box borderLeft="1px solid" borderLeftColor="border.default" height="2em" />;
}

const defaultPageSizeOptions = [10, 20, 30, 40, 50];
function PageSizeSelector(props: {
  value: number;
  setValue: (updater: number | ((pageSize: number) => number)) => void;
  options?: number[];
}) {
  // Sort numerically before sortedUniq (which only dedupes *consecutive* equal
  // values): the current pageSize is appended and is usually already in the list,
  // so without the sort it renders a second time at the end (e.g. "10 … 10").
  const options = sortedUniq([...(props.options ?? defaultPageSizeOptions), props.value].sort((a, b) => a - b));

  return (
    <Box display="flex" alignItems="center" sx={{ gap: '0.5em' }}>
      Rows on page
      <select
        value={props.value}
        onChange={e => {
          props.setValue(Number(e.target.value));
        }}
      >
        {options.map(o => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </Box>
  );
}

const ChevronIconRight = () => (
  <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.27398 0L0.333984 0.94L3.38732 4L0.333984 7.06L1.27398 8L5.27398 4L1.27398 0Z" fill="currentColor" />
    <path d="M5.66656 0L4.72656 0.94L7.7799 4L4.72656 7.06L5.66656 8L9.66656 4L5.66656 0Z" fill="currentColor" />
  </svg>
);

const ChevronIconLeft = () => (
  <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.72602 8L9.66602 7.06L6.61268 4L9.66602 0.94L8.72601 8.21774e-08L4.72602 4L8.72602 8Z"
      fill="currentColor"
    />
    <path
      d="M4.33344 8L5.27344 7.06L2.2201 4L5.27344 0.94L4.33344 8.21774e-08L0.333437 4L4.33344 8Z"
      fill="currentColor"
    />
  </svg>
);

const Root = styled.div`
  display: flex;
  align-items: center;
`;

interface ButtonProps {
  enabled: boolean;
}

const buttonHeightEm = 2;
const buttonBorderRadiusEm = 0.6;
const buttonMarginEm = 0.3;
const textButtonPaddingEm = 0.7;

const JumpToEdgeButton = styled.button<ButtonProps>`
  ${props => getButtonCommonStyle(props.enabled)}
  border-radius: ${buttonBorderRadiusEm}em;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${buttonHeightEm}em;
  div {
    font-size: 1.6em;
  }
`;

const PrevButton = styled.button<ButtonProps>`
  ${props => getButtonCommonStyle(props.enabled)}
  border-radius: ${buttonBorderRadiusEm}em 0 0 ${buttonBorderRadiusEm}em;
  margin-left: ${buttonMarginEm}em;
  padding: 0 ${textButtonPaddingEm}em;
`;

const NextButton = styled.button<ButtonProps>`
  ${props => getButtonCommonStyle(props.enabled)}
  border-radius: 0 ${buttonBorderRadiusEm}em ${buttonBorderRadiusEm}em 0;
  margin-right: ${buttonMarginEm}em;
  padding: 0 ${textButtonPaddingEm}em;
`;

function getButtonCommonStyle(enabled: boolean) {
  return css`
    font-weight: 500;
    background-color: ${BouColor.PLATFORM_BG_WHITE};
    border: 1px solid ${BouColor.PLATFORM_BORDER_GRAY_LIGHTER};
    height: ${buttonHeightEm}em;
    color: ${enabled ? BouColor.PLATFORM_TEXT_BLACK : BouColor.PLATFORM_TEXT_GRAY_LIGHTER};
    user-select: none;
    -webkit-user-select: none;
    :hover {
      color: ${enabled ? BouColor.TOOL_HOVER_DARK_GRAY : BouColor.PLATFORM_TEXT_GRAY_LIGHTER};
    }
    :active {
      color: ${enabled ? BouColor.TOOL_HOVER_DARK_GRAY : BouColor.PLATFORM_TEXT_GRAY_LIGHTER};
    }
    cursor: ${enabled ? 'pointer' : 'auto'};
  `;
}

const PageControlWrap = styled.div`
  margin-left: 1em;
  margin-right: 1em;

  // Disable text selections that would otherwise happen with multiple clicks
  user-select: none;
  -webkit-user-select: none;
`;
